import { useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { cognitoConfig } from '../../lib/amplify';
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";

// ログインフォーム
const SignInForm = ({ onSubmit, isLoading }: { onSubmit: (email: string, password: string) => Promise<void>, isLoading: boolean }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Input
          type="email"
          name="email"
          placeholder="メールアドレス"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
        <Input
          type="password"
          name="password"
          placeholder="パスワード"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'ログイン中...' : 'ログイン'}
      </Button>
    </form>
  );
};

// 新規登録フォーム
// 利用規約ダイアログコンポーネント
const TermsDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link" className="h-auto p-0 underline">利用規約</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>モニター利用規約</DialogTitle>
        </DialogHeader>
        <div className="prose prose-sm max-w-none">
          <h2>第1条（目的）</h2>
          <p>本契約は、当社が提供するサービス「こんたくん」（以下「本サービス」という）のモニタリングを利用者が行うことに関する条件を定めることを目的とします。</p>

          <h2>第2条（契約期間）</h2>
          <p>1. 本契約の期間は、契約締結日から3ヶ月間とします。</p>
          <p>2. 契約期間満了の1週間前までに、当社または利用者いずれからも書面による異議の申し出がない場合は、同一条件でさらに1ヶ月間延長されるものとし、以後も同様とします。</p>

          <h2>第3条（モニターの役割と責任）</h2>
          <p>1. 利用者は、本サービスを実際に使用し、以下の項目について評価・フィードバックを提供するものとします：</p>
          <ul>
            <li>サービスの使いやすさ</li>
            <li>機能の有用性</li>
            <li>不具合や改善点の報告</li>
            <li>その他気付いた点</li>
          </ul>
          <p>2. 利用者は、月に1回以上のフィードバックを当社に提供することとします。</p>
          <p>3. 利用者は、本サービスの利用およびフィードバックの提供において、誠実に行動するものとします。</p>

          <h2>第4条（守秘義務）</h2>
          <p>1. 利用者は、本契約に基づき知り得た当社の技術上、営業上の一切の秘密情報を、第三者に開示または漏洩してはならないものとします。</p>
          <p>2. 本条の義務は、本契約終了後も3年間継続するものとします。</p>

          <h2>第5条（報酬および免責事項）</h2>
          <p>1. 本契約期間中、利用者は本サービスを無償で利用できるものとします。</p>
          <p>2. その他の報酬については、当社と利用者の協議の上で別途定めることができるものとします。</p>
          <p>3. 本サービスはモニター版であり、以下の事項について利用者は予め承諾するものとします：</p>
          <ul>
            <li>本サービスの利用に起因して利用者に生じた一切の損害（データの損失、業務の中断、その他の金銭的損失を含むがこれらに限定されない）について、当社は免責されるものとします。</li>
            <li>モニター版は開発段階のサービスであり、予告なく機能の変更や停止が行われる可能性があります。</li>
            <li>利用者は、自己の責任と判断において本サービスを利用するものとします。</li>
          </ul>

          <h2>第6条（契約の解除）</h2>
          <p>1. 当社または利用者は、相手方が本契約に違反し、相当の期間を定めて催告したにもかかわらず、その違反を是正しない場合には、本契約を解除することができます。</p>
          <p>2. 前項の規定にかかわらず、当社は、利用者が1ヶ月以上フィードバックを提供しない場合、催告なしに本契約を解除することができます。</p>

          <h2>第7条（協議事項）</h2>
          <p>本契約に定めのない事項または本契約の解釈に疑義が生じた場合は、当社と利用者が誠意をもって協議の上、解決するものとします。</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SignUpForm = ({ onSubmit, isLoading }: { onSubmit: (email: string, password: string) => Promise<void>, isLoading: boolean }) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // パスワードの各要件の状態を管理
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  // パスワードの強度を0-100で計算
  const calculatePasswordStrength = (reqs: typeof passwordRequirements) => {
    const metRequirements = Object.values(reqs).filter(Boolean).length;
    return (metRequirements / 4) * 100;
  };

  // パスワード要件のチェック
  const checkPasswordRequirements = (pass: string) => {
    setPasswordRequirements({
      length: pass.length >= 8,
      uppercase: /[A-Z]/.test(pass),
      lowercase: /[a-z]/.test(pass),
      number: /[0-9]/.test(pass)
    });
  };

  // パスワード入力時に要件をチェック
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordRequirements(newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (!agreedToTerms) {
      setError('利用規約への同意が必要です');
      return;
    }

    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Input
          type="email"
          name="email"
          placeholder="メールアドレス"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
        <div className="space-y-1">
          <Input
            type="password"
            name="new-password"
            placeholder="パスワード"
            value={password}
            onChange={handlePasswordChange}
            autoComplete="new-password"
            required
          />
          {/* パスワード強度インジケーター */}
          {password && (
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${calculatePasswordStrength(passwordRequirements)}%`,
                  backgroundColor: calculatePasswordStrength(passwordRequirements) < 50 ? '#ef4444' : 
                                 calculatePasswordStrength(passwordRequirements) < 100 ? '#eab308' : '#22c55e'
                }}
              />
            </div>
          )}
          {/* パスワード要件チェックリスト */}
          <ul className="text-sm space-y-1 mt-2">
            <li className={`flex items-center space-x-2 ${passwordRequirements.length ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.length ? '✓' : '○'} 8文字以上
            </li>
            <li className={`flex items-center space-x-2 ${passwordRequirements.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.uppercase ? '✓' : '○'} 大文字（A-Z）を1文字以上
            </li>
            <li className={`flex items-center space-x-2 ${passwordRequirements.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.lowercase ? '✓' : '○'} 小文字（a-z）を1文字以上
            </li>
            <li className={`flex items-center space-x-2 ${passwordRequirements.number ? 'text-green-600' : 'text-gray-500'}`}>
              {passwordRequirements.number ? '✓' : '○'} 数字（0-9）を1文字以上
            </li>
          </ul>
        </div>
        <Input
          type="password"
          name="confirm-password"
          placeholder="パスワード（確認）"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={agreedToTerms}
          onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
        />
        <label htmlFor="terms" className="text-sm text-gray-600">
          <TermsDialog />に同意する
        </label>
      </div>

      <Button type="submit" className="w-full" disabled={isLoading || !agreedToTerms}>
        {isLoading ? '登録中...' : '新規登録'}
      </Button>
    </form>
  );
};

// メール確認コード入力フォーム
const ConfirmationForm = ({ 
  onSubmit, 
  onResend, 
  email, 
  isLoading 
}: { 
  onSubmit: (code: string) => Promise<void>, 
  onResend: () => void, 
  email: string, 
  isLoading: boolean 
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await onSubmit(code);
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CardDescription>
        {email}宛に確認コードを送信しました。
        メールに記載されているコードを入力してください。
      </CardDescription>

      <Input
        type="text"
        name="confirmation-code"
        placeholder="確認コード"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? '確認中...' : '確認'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onResend}
          disabled={isLoading}
        >
          コードを再送信
        </Button>
      </div>
    </form>
  );
};

// 統合された認証コンポーネント
const Auth = () => {
  const [activeTab, setActiveTab] = useState('signin');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);

  const { signIn, signUp, error: authError } = useAuthContext();

  const handleSignIn = async (email: string, password: string) => {
    const success = await signIn({ email, password });
    if (!success) {
      throw new Error(authError || '認証に失敗しました');
    }
  };

  const handleSignUp = async (email: string, password: string) => {
    try {
      const needsConfirmation = await signUp({ email, password });
      console.log('新規登録結果:', { needsConfirmation, email });
      // サインアップが成功し、確認が必要な場合（needsConfirmation = true）
      if (needsConfirmation) {
        setConfirmationEmail(email);
      } else {
        throw new Error(authError || '登録に失敗しました');
      }
    } catch (error) {
      console.error('新規登録エラー:', error);
      throw error;
    }
  };

  const handleConfirmSignUp = async (code: string) => {
    if (!confirmationEmail) return;
    
    setIsLoading(true);
    try {
      const { confirmSignUp } = await import('@aws-amplify/auth');
      await confirmSignUp({
        ...cognitoConfig,
        username: confirmationEmail,
        confirmationCode: code
      });
      // 確認成功後、ログインタブに切り替え
      setConfirmationEmail(null);
      setActiveTab('signin');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!confirmationEmail) return;
    
    setIsLoading(true);
    try {
      const { resendSignUpCode } = await import('@aws-amplify/auth');
      await resendSignUpCode({
        username: confirmationEmail
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (confirmationEmail) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle>メールアドレスの確認</CardTitle>
        </CardHeader>
        <CardContent>
          <ConfirmationForm
            onSubmit={handleConfirmSignUp}
            onResend={handleResendCode}
            email={confirmationEmail}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>こんたくん</CardTitle>
        <CardDescription>
          お問い合わせフォームのAIアシスタント
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">ログイン</TabsTrigger>
            <TabsTrigger value="signup">新規登録</TabsTrigger>
          </TabsList>
          <TabsContent value="signin">
            <SignInForm onSubmit={handleSignIn} isLoading={isLoading} />
          </TabsContent>
          <TabsContent value="signup">
            <SignUpForm onSubmit={handleSignUp} isLoading={isLoading} />
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button variant="link" onClick={() => console.log('TODO: パスワードリセット')}>
          パスワードをお忘れの方
        </Button>
      </CardFooter>
    </Card>
  );
};

export default Auth;
